import type { DebugConfig, DebugLayer } from '../config/debug.config';

export type DebugListener = (manager: DebugManager) => void;

/** Debug state: overall toggle + individual visualisation layers. */
export class DebugManager {
  private enabledFlag: boolean;
  private readonly layers: Record<DebugLayer, boolean>;
  private readonly listeners = new Set<DebugListener>();

  constructor(config: DebugConfig, startEnabled: boolean) {
    this.enabledFlag = startEnabled;
    this.layers = { ...config.defaultLayers };
  }

  get enabled(): boolean {
    return this.enabledFlag;
  }

  toggle(): void {
    this.setEnabled(!this.enabledFlag);
  }

  setEnabled(enabled: boolean): void {
    if (this.enabledFlag === enabled) return;
    this.enabledFlag = enabled;
    this.notify();
  }

  layer(layer: DebugLayer): boolean {
    return this.layers[layer];
  }

  /** Layer is drawn only if debug mode is on AND the layer is enabled. */
  isVisible(layer: DebugLayer): boolean {
    return this.enabledFlag && this.layers[layer];
  }

  setLayer(layer: DebugLayer, visible: boolean): void {
    if (this.layers[layer] === visible) return;
    this.layers[layer] = visible;
    this.notify();
  }

  onChange(listener: DebugListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener(this);
  }
}
